import type { ExternalToast } from '@forgd/ui-kit/sonner'
import { toast } from '@forgd/ui-kit/sonner'

type Status = Extract<keyof typeof toast, 'success' | 'info' | 'warning' | 'error'>

export interface AppToastOptions extends Omit<ExternalToast, 'description'> {
  title?: string
  description?: string
}

type AppToastInput = string | AppToastOptions

function normalize(input: AppToastInput): { title: string, options: ExternalToast } {
  if (typeof input === 'string') {
    return { title: input, options: {} }
  }
  const { title, ...options } = input
  return { title: title ?? '', options }
}

/**
 * Show application-level notifications
 *
 * @see https://github.com/forged-com/forgd/pull/1715
 */
export function useAppToast() {
  // this may be getting called after the nuxt app context is lost
  const nuxtApp = tryUseNuxtApp()
  if (!nuxtApp) {
    // noop
    const noopHandler = () => {
      if (import.meta.dev) {
        throw new Error('useAppToast() must be called before an async context.')
      }
    }
    return {
      success: noopHandler,
      info: noopHandler,
      warning: noopHandler,
      error: noopHandler,
      clear: noopHandler,
    }
  }

  function add(type: Status) {
    return (input: AppToastInput) => {
      const { title, options } = normalize(input)
      toast[type](title, options)
    }
  }

  return {
    success: add('success'),
    info: add('info'),
    warning: add('warning'),
    error: add('error'),
    clear: () => toast.dismiss(),
  }
}
